import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonEssayQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";

interface IEssayQuestion {
  question: Question;
  participantFieldId: string;
}

const EssayQuestion = ({
  question,
  participantFieldId
}: IEssayQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;
  const error = errors[question.id] || {};
  const answer = answers[question.id]?.value ?? "";
  const { locale } = useAppSelector((state) => state.site.site);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: e.target.value }
      })
    );
  };

  return (
    <CommonEssayQuestion
      layoutProps={{ width: "100%" }}
      invalid={!!error.code}
      required={question.required}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={answer}
      questionId={question?.id}
      disabled={saving}
      changeEventHandler={handleChange}
      contentClassName="ehq-surveyTextareaFeild"
      size="lg"
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      maxLength={question?.maxLength || 20_000}
      maxNote={translate("common.MaxLengthHelper", {
        count: Intl.NumberFormat(locale).format(question?.maxLength || 20_000)
      })}
      locale={locale}
      answerDataTestId={surveyParticipantFieldControl(
        participantFieldId,
        "essay-textarea"
      )}
    />
  );
};

export default EssayQuestion;
